/**
 * API Core - Base types, helpers, and request utilities
 *
 * Headers are injected at runtime via setApiRequestHeaders(), called by
 * PurchaseFlowConfigProvider on mount.  This avoids threading headers
 * through every API function signature.
 */

// ============================================================================
// Types
// ============================================================================

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: Record<string, unknown>;
}

/**
 * JSON error body returned by the API (snake_case).
 * Different endpoints may return a subset of these fields.
 */
export interface ApiErrorResponse {
  error_message?: string;
  error?: string;
  error_name?: string;
  error_type?: string;
  error_title?: string;
  recoverable?: boolean;
  retriable?: boolean;
  error_origin?: string;
  error_pathname?: string;
  id?: number;
  [key: string]: unknown;
}

/**
 * Error thrown by apiRequest when the response is not ok.
 * Use userMessage for UI; message includes status prefix for logging.
 */
export class ApiError extends Error {
  readonly status: number;

  readonly errorData: ApiErrorResponse | null;

  /** User-facing message (e.g. error_message from JSON), no "API request failed: 422" prefix */
  readonly userMessage: string;

  constructor(
    message: string,
    status: number,
    userMessage: string,
    errorData: ApiErrorResponse | null = null
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.userMessage = userMessage;
    this.errorData = errorData;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/** Type guard: value is a non-null object (e.g. parsed JSON). */
function isJsonObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Parse JSON error body into ApiErrorResponse. */
function parseErrorResponse(data: unknown): ApiErrorResponse | null {
  if (!isJsonObject(data)) return null;
  return data as ApiErrorResponse;
}

/** Get user-facing message from ApiErrorResponse. */
function getUserMessage(data: ApiErrorResponse): string {
  return data.error_message ?? data.error ?? JSON.stringify(data);
}

// ============================================================================
// Module-level header injection
// ============================================================================

type RequestHeadersFn = () => Record<string, string> | Promise<Record<string, string>>;

let requestHeadersFn: RequestHeadersFn | undefined;

/**
 * Called by PurchaseFlowConfigProvider to register (or clear) the
 * host-app's header factory.
 */
export const setApiRequestHeaders = (fn: RequestHeadersFn | undefined): void => {
  requestHeadersFn = fn;
};

/**
 * Resolves the current extra headers.  Returns {} when no factory is set.
 */
const getExtraHeaders = async (): Promise<Record<string, string>> => {
  if (!requestHeadersFn) return {};
  return requestHeadersFn();
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build API URL using window.endpoint as base.
 * Used by apiRequest and by ChargePayPal for popup URLs.
 */
export const buildApiUrl = (path: string): string => {
  const baseUrl = window.endpoint;
  return `${ baseUrl }${ path }`;
};

// ============================================================================
// Request
// ============================================================================

/**
 * Make API request with common error handling.
 * Merges host-app headers (via setApiRequestHeaders) into every request.
 */
export const apiRequest = async <T>(
  url: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const {
    method = 'GET',
    body
  } = options;

  const fullUrl = buildApiUrl(url);
  const extraHeaders = await getExtraHeaders();

  try {
    const response = await fetch(fullUrl, {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...extraHeaders
      },
      ...(body && { body: JSON.stringify(body) })
    });

    if (!response.ok) {
      // Try to parse JSON error response (for validation errors like 422)
      let errorText: string;
      let errorData: ApiErrorResponse | null = null;

      try {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const raw = await response.json();
          errorData = parseErrorResponse(raw);
          errorText = errorData ? getUserMessage(errorData) : JSON.stringify(raw);
        } else {
          errorText = await response.text();
        }
      } catch {
        errorText = response.statusText || `HTTP ${ response.status }`;
      }

      const fullMessage = `API request failed: ${ response.status } ${ errorText }`;
      const userMessage = errorData ? getUserMessage(errorData) : errorText;
      throw new ApiError(fullMessage, response.status, userMessage, errorData);
    }

    const rawResponse = await response.json();
    // Use response as-is (snake_case) - no case conversion needed
    return rawResponse as T;
  } catch (err) {
    if (err instanceof TypeError && err.message.includes('fetch')) {
      throw new Error(`Network error: Unable to reach server. Check if ${ fullUrl } is accessible.`);
    }
    throw err;
  }
};
